import './polyfills'; // must run before any dependency that uses ES2022 features (see polyfills.ts)
import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import { MantineProvider, createTheme } from '@mantine/core';
import { LazyToaster } from '@services/toast';
import { router } from './router';
import './reset.css';
import '@mantine/core/styles.css';
import 'react-confirm-alert/src/react-confirm-alert.css';
import './styles.css';
import { i18nReady } from './i18n';
import { getBrowserId } from '@services/browser';
import { initTelemetry } from '@services/telemetry';
import { bootstrapAuth } from '@services/auth';
import { captureAdClickIds } from '@services/adAttribution';
import { warmupFunctions } from '@services/functionsWarmup';
import { ThemeVariablesProvider } from './contexts/ThemeVariablesContext';
import { initializeGoogleTags } from '@services/googleTags';
import { initializePwaInstallTracking } from '@services/pwaInstallTracking';
import { initializePrivacyStorageCleanup } from '@services/privacyStorageCleanup';

const theme = createTheme({
  /** Mantine theme override here */
});

// Capture click ids before rendering or deferred startup: SPA navigation can remove the landing
// query string before an idle callback gets a chance to persist it.
try {
  captureAdClickIds();
} catch {
  // Attribution is non-critical; startup must continue if storage is unavailable.
}

// Consent-controlled tags are initialized before React renders so defaults are queued before any
// config command and a stored choice applies on the first page view. The temporary Advanced
// Consent Mode path may load Google's tag with every optional signal denied; App Insights and
// identifier-bearing attribution remain strictly opt-in.
try {
  initializePrivacyStorageCleanup();
} catch {
  // Privacy cleanup is non-critical and always fails closed.
}
try {
  initializeGoogleTags();
} catch {
  // Advertising measurement is non-critical and always fails closed.
}
try {
  initializePwaInstallTracking();
} catch {
  // Install measurement is non-critical and always fails closed.
}

function scheduleNonCriticalStartup() {
  const run = () => {
    try {
      getBrowserId();
    } catch {
      // ignore
    }

    try {
      initTelemetry();
    } catch {
      // ignore
    }

    void bootstrapAuth();
  };

  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    (window as any).requestIdleCallback(run, { timeout: 5000 });
  } else if (typeof window !== 'undefined') {
    setTimeout(run, 0);
  } else {
    // Fallback for non-browser environments (should not normally happen here)
    run();
  }
}

function scheduleServiceWorkerRegistration() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  if (!('serviceWorker' in navigator)) {
    return;
  }

  const isProdBuild = import.meta.env.PROD;
  if (!isProdBuild) {
    void navigator.serviceWorker
      .getRegistrations()
      .then((regs) => Promise.all(regs.map((r) => r.unregister())))
      .catch(() => {});
    return;
  }

  const host = (window.location.hostname || '').toLowerCase();
  const isLocalhost =
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === '::1' ||
    host.endsWith('.localhost');
  const isCanonical = isLocalhost || host === 'sendallfiles.com';
  if (!isCanonical) {
    return;
  }

  const register = () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  };

  if (document.readyState === 'complete') {
    register();
  } else {
    window.addEventListener(
      'load',
      () => {
        register();
      },
      { once: true },
    );
  }
}

function schedulePingWarmupAfterLoad() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  const trigger = () => {
    setTimeout(() => {
      try {
        warmupFunctions();
      } catch {
        // ignore
      }
    }, 200);
  };

  if (document.readyState === 'complete') {
    trigger();
  } else {
    window.addEventListener('load', () => {
      trigger();
    }, { once: true });
  }
}

function RootShell() {
  return (
    <>
      <ThemeVariablesProvider>
        <MantineProvider theme={theme}>
          <RouterProvider router={router} />
          <LazyToaster richColors position="bottom-right" expand />
        </MantineProvider>
      </ThemeVariablesProvider>
    </>
  );
}

const rootElement = document.getElementById('root') as HTMLElement;
const root = ReactDOM.createRoot(rootElement);

void i18nReady.then(() => {
  root.render(
    <React.StrictMode>
      <RootShell />
    </React.StrictMode>,
  );

  scheduleNonCriticalStartup();
  schedulePingWarmupAfterLoad();
  scheduleServiceWorkerRegistration();
});
